/**
 * TimestampVariant - Variante base para Date
 *
 * Representa data como milissegundos desde epoch (como JavaScript Date.getTime())
 *
 * @example
 * 1733425800000 // 2024-12-05T19:30:00.000Z
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { DateTimestamp, DateFormatOptions } from "../types";

export class TimestampVariant extends BaseVariant<
  DateTimestamp,
  DateTimestamp
> {
  readonly name = "timestamp";
  readonly type = "number" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: DateTimestamp): DateTimestamp {
    return value;
  }

  fromBase(base: DateTimestamp): DateTimestamp {
    return base;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): DateTimestamp | null {
    // Já é número
    if (typeof input === "number") {
      if (!Number.isFinite(input)) return null;
      return input;
    }

    // String numérica
    if (typeof input === "string") {
      const trimmed = input.trim();

      // String numérica pura
      if (/^-?\d+$/.test(trimmed)) {
        const num = parseInt(trimmed, 10);
        if (Number.isFinite(num)) return num;
      }

      // Tenta parsear como ISO date
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }

      return null;
    }

    // Date object
    if (input instanceof Date) {
      const ts = input.getTime();
      return isNaN(ts) ? null : ts;
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: DateTimestamp): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "number") {
      errors.push("Timestamp must be a number");
      return { valid: false, errors };
    }

    if (!Number.isFinite(value)) {
      errors.push("Timestamp must be a finite number");
      return { valid: false, errors };
    }

    if (!Number.isInteger(value)) {
      errors.push("Timestamp must be an integer");
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: DateTimestamp, options?: FormatOptions): string {
    const opts = options as DateFormatOptions | undefined;
    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Se não especificar opções, retorna o timestamp como string
    if (
      !opts ||
      (!opts.dateStyle && !opts.timeStyle && !opts.dateOnly && !opts.timeOnly)
    ) {
      return String(value);
    }

    const locale = opts.locale || "en-US";
    const formatOptions: Intl.DateTimeFormatOptions = {};

    if (opts.timeZone) {
      formatOptions.timeZone = opts.timeZone;
    }

    if (opts.dateOnly) {
      formatOptions.dateStyle = opts.dateStyle || "medium";
    } else if (opts.timeOnly) {
      formatOptions.timeStyle = opts.timeStyle || "medium";
    } else {
      if (opts.dateStyle) formatOptions.dateStyle = opts.dateStyle;
      if (opts.timeStyle) formatOptions.timeStyle = opts.timeStyle;
    }

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  }
}
